import { Asset } from '@/api/generatedApi';
import { useMoveAsset } from '@/hooks/api/assets/useMoveAsset';
import { useGetFolders } from '@/hooks/useGetFolders';
import { useToast } from '@/hooks/useToast';
import { getAssetFileName } from '@/utils/assets';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useMemo, useState, useRef } from 'react';
import { FolderTree } from '../MoveFolderModal/FolderTree';
import { buildFolderTree } from '@/utils/buildFolderTree';

export const MoveAssetModal = ({
  isOpen,
  onClose,
  asset,
  preselectedTargetFolderId,
  onMoved,
}: {
  isOpen: boolean;
  onClose: () => void;
  asset?: Asset;
  preselectedTargetFolderId?: string | null;
  onMoved?: () => void;
}) => {
  const { showToast } = useToast();
  const { folders, isLoading: isFoldersLoading } = useGetFolders({ enabled: isOpen });

  const initialSelectedFolderId = useMemo(() => {
    if (!asset) return;
    if (preselectedTargetFolderId === null) return;
    if (preselectedTargetFolderId !== undefined) return preselectedTargetFolderId;
    return asset.folderId ?? undefined;
  }, [asset, preselectedTargetFolderId]);

  const [selectedFolderId, setSelectedFolderId] = useState<string | undefined>(initialSelectedFolderId);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const hasInitialized = useRef(false);

  if (isOpen && asset && folders.length > 0 && !hasInitialized.current) {
    hasInitialized.current = true;
    setSelectedFolderId(initialSelectedFolderId);
    setErrorMessage(undefined);
    setExpandedItems(folders.map(f => f.id));
  }

  if (!isOpen && hasInitialized.current) {
    hasInitialized.current = false;
  }

  const { moveAsset, isMoving } = useMoveAsset();

  const folderTree = useMemo(() => buildFolderTree(folders, { sort: true }), [folders]);

  const handleSelect = (_event: React.SyntheticEvent | null, itemId: string | null) => {
    if (itemId === 'root') {
      setSelectedFolderId(undefined);
    } else if (itemId) {
      setSelectedFolderId(itemId);
    }
  };

  const handleSubmit = () => {
    if (!asset) return;

    const currentIsRoot = asset.folderId === undefined || asset.folderId === null;
    const targetIsRoot = selectedFolderId === undefined;

    if (targetIsRoot && currentIsRoot) {
      setErrorMessage('Asset is already in this location.');
      return;
    }
    if (selectedFolderId === asset.folderId) {
      setErrorMessage('Asset is already in this location.');
      return;
    }

    setErrorMessage(undefined);

    const originalFolderId = asset.folderId ?? undefined;

    moveAsset(
      { asset, folderId: selectedFolderId },
      {
        onSuccess: () => {
          showToast({
            message: `Moved "${getAssetFileName(asset)}"`,
            type: 'success',
            action: {
              label: 'Undo',
              onClick: () => {
                moveAsset({
                  asset: { ...asset, folderId: selectedFolderId },
                  folderId: originalFolderId,
                });
              },
            },
          });
          onMoved?.();
          onClose();
        },
        onError: (error: unknown) => {
          const axiosError = error as { response?: { data?: { message?: string } } };
          setErrorMessage(axiosError.response?.data?.message ?? 'Failed to move asset');
        },
      }
    );
  };

  const handleClose = () => {
    if (!isMoving) {
      onClose();
    }
  };

  const currentIsRoot = asset?.folderId === undefined || asset?.folderId === null;
  const targetIsRoot = selectedFolderId === undefined;
  const isSameLocation = (targetIsRoot && currentIsRoot) || selectedFolderId === asset?.folderId;
  const canSubmit = !isMoving && !isSameLocation;

  const getDestinationName = () => {
    if (selectedFolderId === undefined) return 'Assets home folder';
    return folders.find(f => f.id === selectedFolderId)?.name ?? 'Unknown';
  };

  const isConfirmationMode = preselectedTargetFolderId !== undefined;

  return (
    <Dialog
      fullWidth
      maxWidth='sm'
      open={isOpen}
      onClose={handleClose}
      aria-modal='true'
      slotProps={{
        paper: {
          sx: {
            borderRadius: '12px',
          },
        },
      }}
    >
      <DialogTitle>Move Asset</DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'visible',
          '&:first-of-type': {
            paddingTop: 3,
          },
          ...(!isConfirmationMode && {
            minHeight: 300,
          }),
        }}
      >
        {errorMessage && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}
        {isConfirmationMode ? (
          <Box>
            <Typography>
              Move <strong>{asset ? getAssetFileName(asset) : ''}</strong> to <strong>{getDestinationName()}</strong>?
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              Select a destination for <strong>{asset ? getAssetFileName(asset) : ''}</strong>
            </Typography>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1,
                maxHeight: 300,
                overflow: 'auto',
              }}
            >
              <FolderTree
                folderTree={folderTree}
                selectedFolderId={selectedFolderId}
                expandedItems={expandedItems}
                disabledFolderIds={new Set()}
                isLoading={isFoldersLoading}
                onSelect={handleSelect}
                onExpandedItemsChange={(_event, itemIds) => setExpandedItems(itemIds)}
              />
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isMoving}>
          Cancel
        </Button>
        <Button variant='contained' onClick={handleSubmit} disabled={!canSubmit} sx={{ width: '100px' }}>
          {isMoving ? <CircularProgress size={20} /> : 'Move'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
